import { useState } from 'react';
import { Modal, ModalBody, ModalButton, ModalFooter, ModalHeader } from '@tigergraph/app-ui-lib/modal';
import { Spinner } from '@tigergraph/app-ui-lib/spinner';
import { useStyletron } from '@tigergraph/app-ui-lib/Theme';

import { showToast } from '@/components/styledToasterContainer';
import { useMutationRefreshWorkspace } from '@/pages/workgroup/hook';
import { WorkspaceT, isRefreshIng, isStatusActive, isStatusPaused } from '@/pages/workgroup/type';
import { getErrorMessage } from '@/utils/utils';

export function SnapshotTag({ snapshot, rwWorkspace }: { snapshot: WorkspaceT; rwWorkspace?: WorkspaceT }) {
  const [css] = useStyletron();
  const [showConfirm, setShowConfirm] = useState(false);
  const refreshMutation = useMutationRefreshWorkspace();

  const canUpdateFromRw = rwWorkspace ? isStatusActive(rwWorkspace.status) : true;
  const enableUpdate = canUpdateFromRw && !isStatusPaused(snapshot.status);
  const isRefreshing = isRefreshIng(snapshot.refresh_status) || refreshMutation.isLoading;

  const onConfirm = () => {
    refreshMutation.mutate(
      {
        group_id: snapshot.workgroup_id,
        space_id: snapshot.workspace_id,
      },
      {
        onSuccess: () => {
          setShowConfirm(false);
        },
        onError: (err) => {
          showToast({
            kind: 'negative',
            message: `${getErrorMessage(err)}`,
          });
        },
      }
    );
  };

  return (
    <>
      <span
        className={css({
          fontSize: '11px',
          fontWeight: 600,
          color: '#D97706',
          backgroundColor: '#FEF3C7',
          borderRadius: '4px',
          padding: '2px 8px',
          lineHeight: '16px',
          cursor: enableUpdate || isRefreshing ? 'pointer' : 'not-allowed',
          opacity: enableUpdate || isRefreshing ? 1 : 0.6,
        })}
        role="button"
        tabIndex={0}
        aria-label="Update snapshot"
        aria-disabled={!enableUpdate}
        onClick={(e) => {
          e.stopPropagation();
          if (!enableUpdate || isRefreshing) {
            return;
          }
          setShowConfirm(true);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            if (!enableUpdate || isRefreshing) {
              return;
            }
            setShowConfirm(true);
          }
        }}
      >
        {isRefreshing ? <Spinner $size="12px" $borderWidth="2px" /> : 'Snapshot'}
      </span>

      <Modal onClose={() => setShowConfirm(false)} isOpen={showConfirm} closeable={!refreshMutation.isLoading}>
        <ModalHeader>Are You Sure You Want To Update The Database?</ModalHeader>
        <ModalBody>{null}</ModalBody>
        <ModalFooter>
          <ModalButton kind="secondary" onClick={() => setShowConfirm(false)} disabled={refreshMutation.isLoading}>
            Cancel
          </ModalButton>
          <ModalButton onClick={onConfirm} isLoading={refreshMutation.isLoading} disabled={!enableUpdate}>
            OK
          </ModalButton>
        </ModalFooter>
      </Modal>
    </>
  );
}
